import React from 'react';
import type { FormikValues } from 'formik';

import { reportAxiosError } from '~/Utils';

import { hasDraftsInvalidatedBySave } from './staleDrafts';

type SetSubmitting = (isSubmitting: boolean) => void;

interface UseStaleDraftsSaveGuardParams {
  // Drafts only exist for organizations with the feature, so nobody else pays for the lookup.
  isEnabled: boolean;
  organizationId: number;
  onSave: (values: FormikValues) => Promise<void>;
}

interface GuardedSaveContext {
  // Taken from the Formik submission so the confirmed save can re-raise the busy state.
  setSubmitting: SetSubmitting;
  incidentConfigurationId: number;
  liveVersion: number;
  // Set when the editor was opened from a draft that this save promotes to live.
  excludedDraftId?: number | null;
}

interface UseStaleDraftsSaveGuardResult {
  guardedSave: (values: FormikValues, context: GuardedSaveContext) => Promise<void>;
  confirmModalProps: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
  };
}

/**
 * Wraps a live incident-configuration save with the "saved drafts will become unavailable" warning.
 *
 * Every screen that PATCHes `incident_configurations/<id>` bumps the same version and invalidates
 * the same drafts, so they all share this guard rather than each re-deriving the lookup, the modal
 * state and the busy-state handling.
 */
export const useStaleDraftsSaveGuard = ({
  isEnabled,
  organizationId,
  onSave,
}: UseStaleDraftsSaveGuardParams): UseStaleDraftsSaveGuardResult => {
  // Set to the submitted values while the confirmation is open, so the save can be replayed
  // unchanged once the editor confirms.
  const [pendingSaveValues, setPendingSaveValues] = React.useState<FormikValues | null>(null);
  // Formik's submit lifecycle is already over by the time the editor answers the modal, so the
  // confirmed save has to drive the busy state itself with the setter from that submission.
  const setSubmittingRef = React.useRef<SetSubmitting | null>(null);

  const runSave = async (values: FormikValues) => {
    try {
      await onSave(values);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const willInvalidateDrafts = async ({
    incidentConfigurationId,
    liveVersion,
    excludedDraftId,
  }: Omit<GuardedSaveContext, 'setSubmitting'>): Promise<boolean> => {
    if (!isEnabled) {
      return false;
    }
    try {
      return await hasDraftsInvalidatedBySave({
        organizationId,
        incidentConfigurationId,
        liveVersion,
        excludedDraftId,
      });
    } catch {
      // The lookup only decides whether to warn. A failed read must not turn into "my
      // configuration won't save", so fall through to the save the editor asked for.
      return false;
    }
  };

  const guardedSave = async (values: FormikValues, { setSubmitting, ...lookup }: GuardedSaveContext) => {
    setSubmittingRef.current = setSubmitting;

    if (await willInvalidateDrafts(lookup)) {
      // Hold the save until the editor confirms - the drafts are only lost once the PATCH lands.
      // Returning here ends the submit lifecycle on purpose: keeping `isSubmitting` true would
      // stack the footer's "Saving..." LoadingDialog on top of the question being asked.
      setPendingSaveValues(values);
      return;
    }
    await runSave(values);
  };

  const handleConfirm = async () => {
    const values = pendingSaveValues;
    setPendingSaveValues(null);
    if (!values) {
      return;
    }
    const setSubmitting = setSubmittingRef.current;
    setSubmitting?.(true);
    try {
      await runSave(values);
    } finally {
      setSubmitting?.(false);
    }
  };

  return {
    guardedSave,
    confirmModalProps: {
      isOpen: pendingSaveValues !== null,
      onClose: () => setPendingSaveValues(null),
      onConfirm: handleConfirm,
    },
  };
};
